import * as React from 'react';
import { Theme, makeStyles, createStyles } from '@bb-ui/react-library';
import { Typography } from '@bb-ui/react-library/dist/components/Typography';
import { IconButton } from '@bb-ui/react-library/dist/components/IconButton';
import { Link, useLocation, matchPath } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthorization } from 'hooks/useAuthorization';
import { AuthorizationService, AuthorizationPermission } from 'hooks/useAuthorization.types';
import { Logo } from 'components/Logo';
import { BbThemeProvider } from '@bb-ui/react-library/dist/components/BbThemeProvider';
import { useAuthContext } from 'contexts/AuthContext';
import { useAppConfigContext } from 'contexts/AppConfigContext';
import { Avatar } from '@bb-ui/react-library/dist/components/Avatar';
import {
  ChevronDown as ExpandMoreIcon,
  ChevronUp as ExpandLessIcon,
  Logout as ExitToAppIcon,
} from '@bb-ui/icons/dist/small';

interface NavItem {
  name: string;
  path: string;
  service?: AuthorizationService;
  permission?: AuthorizationPermission;
  devOnly?: boolean;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    sidebar: {
      gridArea: 'sidebar',
      width: '240px',
      backgroundColor: 'rgba(28, 28, 30, 0.98)',
      color: theme.palette.common.white,
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto',
      overflowX: 'hidden',
      borderRight: '1px solid rgba(255, 255, 255, 0.06)',
      boxShadow: '2px 0 8px rgba(0, 0, 0, 0.15)',
      zIndex: 10,
    },
    logoWrapper: {
      position: 'relative',
      padding: theme.spacing(2),
      borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
    },
    envBadge: {
      position: 'absolute',
      top: '6px',
      right: theme.spacing(2),
      fontSize: '8px',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.8px',
      padding: '2px 5px',
      borderRadius: '3px',
      minWidth: '60px',
      textAlign: 'center',
      color: theme.palette.common.white,
      lineHeight: 1.3,
      whiteSpace: 'nowrap',
      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.3)',
    },
    navSection: {
      marginTop: theme.spacing(1),
    },
    sectionHeader: {
      padding: theme.spacing(0.75, 3),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
      transition: 'background-color 0.15s ease',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.04)',
      },
    },
    sectionTitle: {
      fontSize: '10px',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '1.5px',
      color: 'rgba(255, 255, 255, 0.45)',
    },
    expandIcon: {
      color: 'rgba(255, 255, 255, 0.4)',
      fontSize: '20px',
      padding: 0,
    },
    navList: {
      listStyle: 'none',
      margin: 0,
      padding: theme.spacing(0.5, 1.5),
      overflow: 'hidden',
      transition: 'max-height 0.3s ease, opacity 0.3s ease',
    },
    navListCollapsed: {
      maxHeight: 0,
      opacity: 0,
      padding: 0,
    },
    navListExpanded: {
      maxHeight: '1000px',
      opacity: 1,
    },
    navItem: {
      position: 'relative',
      marginBottom: theme.spacing(0.25),
    },
    navItemActive: {
      '& $navLink': {
        borderLeft: '3px solid rgba(64, 156, 255, 0.9)',
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        color: theme.palette.common.white,
        fontWeight: 600,
        borderRadius: '0 8px 8px 0',
        paddingLeft: 'calc(16px - 3px)',
      },
    },
    navLink: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(1, 2.5),
      color: 'rgba(255, 255, 255, 0.75)',
      textDecoration: 'none',
      fontSize: '13.5px',
      borderRadius: '8px',
      transition: 'all 0.15s ease',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        color: theme.palette.common.white,
      },
    },
    sectionDivider: {
      height: '1px',
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
      margin: '4px 24px',
    },
    sidebarFooter: {
      marginTop: 'auto',
      borderTop: '1px solid rgba(255, 255, 255, 0.08)',
      padding: theme.spacing(2, 2),
    },
    userInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      padding: theme.spacing(1, 1),
      marginBottom: theme.spacing(1),
    },
    userName: {
      fontSize: '13px',
      fontWeight: 500,
      color: 'rgba(255, 255, 255, 0.85)',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    signOutLink: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      padding: theme.spacing(1, 1.5),
      color: 'rgba(255, 255, 255, 0.55)',
      textDecoration: 'none',
      fontSize: '13px',
      borderRadius: '8px',
      transition: 'all 0.15s ease',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        color: 'rgba(255, 255, 255, 0.85)',
      },
    },
    signOutIcon: {
      fontSize: '18px',
    },
  }),
);

export const Sidebar: React.FC = () => {
  const classes = useStyles();
  const location = useLocation();
  const { t } = useTranslation();
  const { hasPermission } = useAuthorization();
  const { user } = useAuthContext();
  const { stage } = useAppConfigContext();

  const isNonProd = stage !== 'prod';

  const envBadgeColor: Record<string, string> = {
    local: 'linear-gradient(135deg, #3b82f6, #a855f7)',
    dev: 'linear-gradient(135deg, #10b981, #0ea5e9)',
    int: 'linear-gradient(135deg, #f59e0b, #ef4444)',
    tp: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
  };

  const [expandedSections, setExpandedSections] = React.useState<{ [key: string]: boolean }>({
    'tenant management': true,
    'fleet & infrastructure': true,
    'release pipeline': true,
    'upgrade operations': true,
    'access & licensing': true,
  });

  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const navGroups: NavGroup[] = [
    {
      title: 'Tenant Management',
      items: [
        {
          name: t('tenantList.pageTitle'),
          path: '/tenants',
          service: 'tenant',
          permission: 'read',
        },
        {
          name: t('learnSites.pageTitle'),
          path: '/learn-site',
          service: 'blackboard.sites',
          permission: 'view',
        },
        {
          name: t('learnClients.pageTitle'),
          path: '/client',
          service: 'blackboard.management',
          permission: 'view',
        },
      ],
    },
    {
      title: 'Fleet & Infrastructure',
      items: [
        {
          name: t('fleetService.pageTitle'),
          path: '/fleets',
          service: 'blackboard.fleets',
          permission: 'view',
        },
        {
          name: t('images.pageTitle'),
          path: '/registry',
          service: 'il4',
          permission: 'read',
        },
      ],
    },
    {
      title: 'Release Pipeline',
      items: [
        {
          name: t('artifactLists.pageTitle'),
          path: '/artifacts',
          service: 'blackboard.artifacts',
          permission: 'view',
        },
        {
          name: t('releaseStages.pageTitle'),
          path: '/release-stage',
          service: 'il4',
          permission: 'read',
        },
        {
          name: t('featureFlagList.pageTitle'),
          path: '/feature-flags',
          service: 'feature-flags',
          permission: 'access',
        },
      ],
    },
    {
      title: 'Upgrade Operations',
      items: [
        {
          name: t('cohorts.title'),
          path: '/cohorts',
          service: 'cohort',
          permission: 'search',
        },
        {
          name: t('jobService.pageTitle'),
          path: '/jobs',
          service: 'il4',
          permission: 'read',
        },
        {
          name: t('executions.pageTitle'),
          path: '/executions',
          service: 'il4',
          permission: 'read',
          // Until the lct-jobs region execution list (lct-jobs#437) is live in every stage
          devOnly: true,
        },
        {
          name: t('massQueryTooling.tabTitle'),
          path: '/query-tooling',
        },
      ],
    },
    {
      title: 'Access & Licensing',
      items: [
        {
          name: t('groupList.pageTitle'),
          path: '/groups',
          service: 'fnds-group',
          permission: 'read',
        },
        {
          name: t('licenseList.pageTitle'),
          path: '/licenses',
          service: 'license',
          permission: 'read',
        },
      ],
    },
  ];

  const isActive = (path: string): boolean => {
    // Exact match for the path
    if (matchPath(location.pathname, { path, exact: true })) {
      return true;
    }
    // Also match if we're on a detail page within this section
    // e.g., /tenants/123 should highlight /tenants
    if (location.pathname.startsWith(path) && path !== '/') {
      return true;
    }
    return false;
  };

  const shouldShowItem = (item: NavItem): boolean => {
    // Check dev-only restriction
    if (item.devOnly && stage === 'prod') {
      return false;
    }
    // Check permission — if no service/permission defined, show to all authenticated users
    if (!item.service || !item.permission) {
      return true;
    }
    return hasPermission(item.service, item.permission);
  };

  return (
    <BbThemeProvider theme="dark">
      <nav className={classes.sidebar}>
        <div className={classes.logoWrapper}>
          <Logo />
          {isNonProd && stage && (
            <span
              className={classes.envBadge}
              style={{
                background: envBadgeColor[stage] || 'linear-gradient(135deg, #6b7280, #4b5563)',
              }}
            >
              {stage}
            </span>
          )}
        </div>

        {navGroups.map((group, groupIndex) => {
          const sectionKey = group.title.toLowerCase();
          const isExpanded = expandedSections[sectionKey];
          const visibleItems = group.items.filter(shouldShowItem);

          // Don't render the section if no items are visible
          if (visibleItems.length === 0) {
            return null;
          }

          return (
            <React.Fragment key={groupIndex}>
              {groupIndex > 0 && <div className={classes.sectionDivider} />}
              <div className={classes.navSection}>
                <div
                  className={classes.sectionHeader}
                  onClick={() => toggleSection(sectionKey)}
                  role="button"
                  aria-expanded={isExpanded}
                >
                  <Typography className={classes.sectionTitle}>{group.title}</Typography>
                  <IconButton className={classes.expandIcon} size="small">
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </div>
                <ul
                  className={`${classes.navList} ${
                    isExpanded ? classes.navListExpanded : classes.navListCollapsed
                  }`}
                >
                  {visibleItems.map((item, itemIndex) => (
                    <li
                      key={itemIndex}
                      className={`${classes.navItem} ${isActive(item.path) ? classes.navItemActive : ''}`}
                    >
                      <Link to={item.path} className={classes.navLink}>
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </React.Fragment>
          );
        })}

        <div className={classes.sidebarFooter}>
          {user && (
            <div className={classes.userInfo}>
              <Avatar size="small" alt={user.nickname} src={user.picture} />
              <Typography className={classes.userName}>{user.nickname}</Typography>
            </div>
          )}
          <Link to="/sign-out" className={classes.signOutLink}>
            <ExitToAppIcon className={classes.signOutIcon} />
            {t('auth.signOut')}
          </Link>
        </div>
      </nav>
    </BbThemeProvider>
  );
};
